import { useInfiniteQuery } from '@tanstack/react-query';
import {
  APIError,
  APIErrorType,
  type GetPortfolioBrandsQueryVariables,
  type GetProductCategoriesQuery,
  type GetProductCategoriesQueryVariables,
  useGetPortfolioBrandsQuery,
  useGetProductCategoriesQuery,
} from '@vizit/api';
import { useDebounce } from '@vizit/hooks';
import type { CategoryItem } from '@vizit/molecules';
import isEmpty from 'lodash.isempty';
import * as React from 'react';
import type { Key } from 'react';

const PAGE_SIZE = 10;

interface UseProductCategoryListProps {
  /**
   * Filter text for searching categories
   *
   * @optional
   * @type {string}
   */
  filterText?: string;
  /**
   * Organization ID to fetch categories for
   *
   * @optional
   * @type {string}
   */
  organizationId?: string;
  /**
   * Portfolio ID to fetch categories for
   *
   * @optional
   * @type {string}
   */
  portfolioId?: string;
  /**
   * Function to handle authentication error
   *
   * @type {(error: APIError) => void}
   */
  onAuthenticationError: (error: APIError) => void;
  /**
   * Function to filter by product category
   *
   * @param productCategoryId - Product category ID to filter by
   * @param productCategoryName - Product category name to filter by
   * @type {(productCategoryId: string, productCategoryName: string) => void}
   */
  onFilterProductCategory: (productCategoryId: string, productCategoryName: string) => void;
}

/**
 * Result interface for the product category list hook
 */
interface UseProductCategoryListResult {
  /**
   * Array of category items with transformed breadcrumbs
   *
   * @type {CategoryItem[]}
   */
  items: CategoryItem[];
  /**
   * Whether the initial data is loading
   *
   * @type {boolean}
   */
  isLoading: boolean;
  /**
   * Whether the next page is being fetched
   *
   * @type {boolean}
   */
  isFetchingNextPage: boolean;
  /**
   * Whether there are more pages to load
   *
   * @type {boolean}
   */
  hasNextPage: boolean;
  /**
   * Current filter text for searching categories
   *
   * @type {string}
   */
  filterText: string;
  /**
   * Function to update the filter text
   *
   * @type {(text: string) => void}
   */
  setFilterText: (text: string) => void;
  /**
   * Function to load more items (next page)
   *
   * @type {() => void}
   */
  loadMore: () => void;
  /**
   * Function to handle the selection change
   *
   * @type {(key: Key | null) => void}
   */
  onSelectionChange: (key: Key | null) => void;
}

/**
 * Hook for managing product category list with infinite scrolling and filtering
 * Uses TanStack Query for proper caching and background updates
 *
 * @returns Object containing category data and control functions
 */
function useProductCategoryList(props: UseProductCategoryListProps): UseProductCategoryListResult {
  const { organizationId, portfolioId, filterText, onAuthenticationError, onFilterProductCategory } = props;

  const [localFilterText, setLocalFilterText] = React.useState(filterText || '');
  const debouncedFilterText = useDebounce(localFilterText, 300);

  const queryVariables: Omit<GetProductCategoriesQueryVariables, 'offset'> = React.useMemo(() => {
    return {
      // @ts-ignore: this is protected by the router and will always have a value.
      organization_id: organizationId,
      // @ts-ignore: this is protected by the router and will always have a value.
      portfolio_id: portfolioId,
      breadcrumbs_like: isEmpty(debouncedFilterText) ? undefined : debouncedFilterText,
      limit: PAGE_SIZE,
      name_like: isEmpty(debouncedFilterText) ? undefined : debouncedFilterText,
      sort_by: 'name',
      sort_direction: 'asc',
      // Limit categories to those used in PDPs or images for the current portfolio
      scope_to_portfolio: true,
    };
  }, [organizationId, portfolioId, debouncedFilterText]);

  const { data, error, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage } = useInfiniteQuery({
    queryKey: ['GetProductCategoriesPages', queryVariables],
    queryFn: async ({ pageParam = 0 }) => {
      const response: GetProductCategoriesQuery = await useGetProductCategoriesQuery.fetcher({
        ...queryVariables,
        offset: pageParam,
      });

      const transformedCategories: CategoryItem[] = (response.data || []).map((category) => ({
        ...category,
        breadcrumbs: category.breadcrumbs.split(','),
      }));

      const hasData = response.data && response.data.length > 0;

      return {
        data: transformedCategories,
        // Only increment cursor if we received data, otherwise return undefined to indicate no more data
        // This was the glitch we were seeing on the internal demo 06.05.2025.
        nextCursor: hasData ? pageParam + 10 : undefined,
      };
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    initialPageParam: 0,
    enabled: !!portfolioId,
  });

  // Flatten all pages into a single items array with a duplicate ID check
  const items = React.useMemo(() => {
    const allPageItems = data?.pages.flatMap((page) => page.data) || [];

    const uniqueItems = allPageItems.reduce((uniqueItems, item) => {
      if (uniqueItems.findIndex((existingItem) => existingItem.id === item.id) === -1) {
        uniqueItems.push(item);
      }
      return uniqueItems;
    }, [] as CategoryItem[]);

    return uniqueItems;
  }, [data]);

  const loadMore = () => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  };

  const handleOnSelectionChange = (key: Key | null) => {
    const productCategoryName = items.find((item) => item.id === key)?.name || '';
    onFilterProductCategory(key as string, productCategoryName);
  };

  React.useEffect(() => {
    if (error instanceof APIError && error.type === APIErrorType.NotAuthenticated) {
      onAuthenticationError(error);
    }
  }, [error, onAuthenticationError]);

  return {
    items,
    isLoading: isLoading,
    isFetchingNextPage: isFetchingNextPage,
    hasNextPage: hasNextPage || false,
    filterText: localFilterText,
    setFilterText: setLocalFilterText,
    loadMore,
    onSelectionChange: handleOnSelectionChange,
  };
}

interface UseBrandListProps {
  /**
   * Filter text for searching brands
   *
   * @optional
   * @type {string}
   */
  filterText?: string;
  /**
   * Portfolio ID to fetch brands for
   *
   * @optional
   * @type {string}
   */
  portfolioId?: string;
  /**
   * Function to handle authentication error
   *
   * @type {(error: APIError) => void}
   */
  onAuthenticationError: (error: APIError) => void;
  /**
   * Function to filter by brand
   *
   * @param brand - Brand name to filter by
   * @type {(brand: string) => void}
   */
  onFilterBrand: (brand: string) => void;
}

/**
 * Result interface for the portfolio brand list hook
 */
interface UseBrandListResult {
  /**
   * Array of brand names
   *
   * @type {string[]}
   */
  items: string[];
  /**
   * Whether the initial data is loading
   *
   * @type {boolean}
   */
  isLoading: boolean;
  /**
   * Whether the next page is being fetched
   *
   * @type {boolean}
   */
  isFetchingNextPage: boolean;
  /**
   * Whether there are more pages to load
   *
   * @type {boolean}
   */
  hasNextPage: boolean;
  /**
   * Current filter text for searching brands
   *
   * @type {string}
   */
  filterText: string;
  /**
   * Function to update the filter text
   *
   * @type {(text: string) => void}
   */
  setFilterText: (text: string) => void;
  /**
   * Function to load more items (next page)
   *
   * @type {() => void}
   */
  loadMore: () => void;
  /**
   * Function to handle the selection change
   *
   * @type {(key: Key | null) => void}
   */
  onSelectionChange: (key: Key | null) => void;
}

/**
 * Hook for managing portfolio brand list with infinite scrolling and filtering
 * Uses TanStack Query for proper caching and background updates
 *
 * @returns Object containing brand data and control functions
 */
function useBrandList(props: UseBrandListProps): UseBrandListResult {
  const { portfolioId, filterText, onAuthenticationError, onFilterBrand } = props;

  const [localFilterText, setLocalFilterText] = React.useState(filterText || '');
  const debouncedFilterText = useDebounce(localFilterText, 300);

  const queryVariables: Omit<GetPortfolioBrandsQueryVariables, 'offset'> = React.useMemo(() => {
    return {
      portfolio_id: portfolioId as string,
      search: isEmpty(debouncedFilterText) ? undefined : debouncedFilterText,
      limit: PAGE_SIZE,
    };
  }, [portfolioId, debouncedFilterText]);

  const { data, error, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage } = useInfiniteQuery({
    queryKey: ['GetPortfolioBrandsPages', queryVariables],
    queryFn: async ({ pageParam = 0 }) => {
      const response = await useGetPortfolioBrandsQuery.fetcher({
        ...queryVariables,
        offset: pageParam,
      })();

      const hasData = response.data && response.data.length > 0;

      return {
        data: response.data || [],
        // Only increment cursor if we received data, otherwise return undefined to indicate no more data
        nextCursor: hasData ? pageParam + 10 : undefined,
      };
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    initialPageParam: 0,
    enabled: !!portfolioId,
  });

  // Flatten all pages into a single items array with a duplicate check
  const items: string[] = React.useMemo(() => {
    const allPageItems = data?.pages.flatMap((page) => page.data) || [];

    const uniqueItems = allPageItems.reduce((uniqueItems, item) => {
      if (!uniqueItems.includes(item)) {
        uniqueItems.push(item);
      }
      return uniqueItems;
    }, [] as string[]);

    return uniqueItems;
  }, [data]);

  const loadMore = () => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  };

  const handleOnSelectionChange = (key: Key | null) => {
    onFilterBrand(key as string);
  };

  React.useEffect(() => {
    if (error instanceof APIError && error.type === APIErrorType.NotAuthenticated) {
      onAuthenticationError(error);
    }
  }, [error, onAuthenticationError]);

  return {
    items,
    isLoading: isLoading,
    isFetchingNextPage: isFetchingNextPage,
    hasNextPage: hasNextPage || false,
    filterText: localFilterText,
    setFilterText: setLocalFilterText,
    loadMore,
    onSelectionChange: handleOnSelectionChange,
  };
}

export { useProductCategoryList, useBrandList };
export type { UseProductCategoryListProps, UseProductCategoryListResult, UseBrandListProps, UseBrandListResult };
